import React, { useEffect, useRef } from 'react';
import { useGame } from '../contexts/GameContext';
import BiddingInterface from './BiddingInterface';

// Game components
import GameHeader from './game/GameHeader';
import RevealPhaseOverlay from './game/RevealPhaseOverlay';
import GameOverOverlay from './game/GameOverOverlay';
import PlayersList from './game/PlayersList';
import CurrentBidDisplay from './game/CurrentBidDisplay';
import MyDiceDisplay from './game/MyDiceDisplay';
import WaitingRoomControls from './game/WaitingRoomControls';
import { WaitingMessage, LastAction } from './game/GameStatus';

// Custom hooks
import {
  useBidDefaults,
  useGameHelpers,
} from '../hooks/useGameLogic';

/**
 * Main game room component that orchestrates the entire game UI
 */
const GameRoom: React.FC = () => {
  const {
    gameState,
    myDice,
    myPlayerId,
    addAIPlayer,
    removeAIPlayer,
    setDiceCount,
    makeBid,
    challenge,
    readyUp,
    leaveGame,
    challengeResult,
    clearChallengeResult,
  } = useGame();

  // Custom hooks for game logic
  const {
    bidQuantity,
    setBidQuantity,
    bidFaceValue,
    setBidFaceValue,
    canBid,
    isMyTurn,
  } = useBidDefaults(gameState, myPlayerId);

  const { isPlaying, isWaiting, isGameOver } = useGameHelpers(gameState);

  // Track previous round number to detect new round
  const prevRoundRef = useRef<number>();

  // Clear challenge result when new round starts (round number increments)
  // This ensures the overlay stays visible until all players ready up
  useEffect(() => {
    if (gameState && challengeResult) {
      // If round number increased, we've started a new round after all players readied up
      if (prevRoundRef.current !== undefined && gameState.round > prevRoundRef.current) {
        clearChallengeResult();
      }
      prevRoundRef.current = gameState.round;
    } else if (gameState) {
      prevRoundRef.current = gameState.round;
    }
  }, [gameState, challengeResult, clearChallengeResult]);

  // Early return if no game state
  if (!gameState) {
    return null;
  }

  // Event handlers
  const handleMakeBid = () => {
    makeBid(bidQuantity, bidFaceValue);
  };

  const handleChallenge = () => {
    challenge();
  };

  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  const hasCurrentBid = !!gameState.currentBid;
  const canChallenge = hasCurrentBid;
  const showBiddingInterface = isPlaying && isMyTurn && myDice.length > 0;
  const showWaitingMessage = isPlaying && !isMyTurn;

  return (
    <div className="h-screen overflow-hidden bg-surface-100 dark:bg-gray-900 flex flex-col p-2">
      {/* Header */}
      <GameHeader gameState={gameState} onLeave={leaveGame} />

      {/* Overlays */}
      {challengeResult && (
        <RevealPhaseOverlay
          gameState={gameState}
          challengeResult={challengeResult}
          myPlayerId={myPlayerId}
          onReadyUp={readyUp}
        />
      )}

      {isGameOver && !challengeResult && (
        <GameOverOverlay gameState={gameState} onNewGame={leaveGame} />
      )}

      {/* Main game content */}
      <div className="flex-1 overflow-y-auto flex flex-col gap-2 min-h-0">
        {/* Players section */}
        <div>
          <PlayersList
            gameState={gameState}
            myPlayerId={myPlayerId}
            isPlaying={isPlaying}
            isWaiting={isWaiting}
          />

          {/* Waiting room controls */}
          {isWaiting && (
            <WaitingRoomControls
              gameState={gameState}
              myPlayerId={myPlayerId}
              dicePerPlayer={gameState.dicePerPlayer}
              canAddAI={gameState.players.length < 6}
              onDiceCountChange={setDiceCount}
              onAddAIPlayer={addAIPlayer}
              onRemoveAIPlayer={removeAIPlayer}
              onReadyUp={readyUp}
            />
          )}
        </div>

        {/* Current bid display - only show during gameplay */}
        {!isWaiting && <CurrentBidDisplay gameState={gameState} />}

        {/* My dice display - only show during gameplay */}
        {!isWaiting && <MyDiceDisplay myDice={myDice} />}

        {/* Bidding interface */}
        {showBiddingInterface && (
          <BiddingInterface
            quantity={bidQuantity}
            faceValue={bidFaceValue}
            onQuantityChange={setBidQuantity}
            onFaceValueChange={setBidFaceValue}
            onMakeBid={handleMakeBid}
            onChallenge={canChallenge ? handleChallenge : undefined}
            canBid={canBid}
            canChallenge={canChallenge}
          />
        )}

        {/* Waiting message */}
        {showWaitingMessage && currentPlayer && (
          <WaitingMessage currentPlayer={currentPlayer} players={gameState.players} />
        )}

        {/* Last action display */}
        {gameState.lastAction && <LastAction lastAction={gameState.lastAction} />}
      </div>
    </div>
  );
};

export default GameRoom;